import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Moon, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const fmtRange = (start, end) => {
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    const opts = { month: "short", day: "numeric" };
    const sy = s.getFullYear(), ey = e.getFullYear();
    const sameYear = sy === ey;
    return `${s.toLocaleDateString(undefined, opts)}${sameYear ? "" : ` ${sy}`} – ` +
           `${e.toLocaleDateString(undefined, opts)}, ${ey}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const inNDays = (n) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};

/** Customer-managed "quiet mode" during vacations. Any active or upcoming
 *  window suppresses pickup-reminder broadcasts + "truck 10 min away" SMS
 *  for this customer. Billing/receipt emails are unaffected.
 */
export function SnoozeCard({ enabled = true }) {
    const [snoozes, setSnoozes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [start, setStart] = useState(todayIso());
    const [end, setEnd] = useState(inNDays(7));
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        if (!enabled) return;
        setLoading(true);
        try {
            const { data } = await api.get("/portal/snoozes");
            setSnoozes(data.snoozes || []);
        } catch {
            // silently skip — portal without token
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [enabled]);

    const add = async () => {
        if (!start || !end) return toast.error("Pick start + end dates");
        if (end < start) return toast.error("End date must be on or after start");
        setSaving(true);
        try {
            const { data } = await api.post("/portal/snoozes", {
                start_date: start, end_date: end, reason: reason.trim(),
            });
            setSnoozes((cur) => [...cur, data.snooze]);
            setShowForm(false);
            setReason("");
            setStart(todayIso());
            setEnd(inNDays(7));
            toast.success("Snooze window added — we'll stay quiet during that time");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add snooze");
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        const prev = snoozes;
        setSnoozes((cur) => cur.filter((s) => s.id !== id));
        try {
            await api.delete(`/portal/snoozes/${id}`);
            toast.success("Snooze removed");
        } catch {
            setSnoozes(prev);
            toast.error("Could not remove snooze");
        }
    };

    // Active/upcoming: end_date >= today
    const today = todayIso();
    const active = snoozes.filter((s) => s.end_date >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date));
    const past = snoozes.filter((s) => s.end_date < today);

    if (!enabled) return null;

    return (
        <Card data-testid="portal-snooze-card">
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <Moon className="w-5 h-5 text-muted-foreground" />
                        <div>
                            <div className="font-heading text-lg font-bold">Going away? Snooze reminders</div>
                            <div className="text-xs text-muted-foreground">
                                We'll skip pickup-reminder texts + "truck near you" alerts during the days you pick.
                                Billing and service still run normally.
                            </div>
                        </div>
                    </div>
                    {!showForm && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowForm(true)}
                            data-testid="portal-snooze-add-btn"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Add
                        </Button>
                    )}
                </div>

                {loading && <div className="text-xs text-muted-foreground">Loading…</div>}

                {active.length > 0 && (
                    <div className="space-y-1.5 mt-2" data-testid="portal-snooze-active-list">
                        {active.map((s) => (
                            <div
                                key={s.id}
                                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-secondary/40 text-sm"
                                data-testid={`portal-snooze-item-${s.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium tabular truncate">{fmtRange(s.start_date, s.end_date)}</div>
                                    {s.reason && (
                                        <div className="text-[11px] text-muted-foreground truncate">{s.reason}</div>
                                    )}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-red-600"
                                    onClick={() => remove(s.id)}
                                    data-testid={`portal-snooze-remove-${s.id}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && active.length === 0 && !showForm && (
                    <div className="text-xs text-muted-foreground italic mt-2">
                        No snoozes scheduled. Add one before your next vacation and we'll go silent for those days.
                    </div>
                )}

                {showForm && (
                    <div className="mt-4 rounded-md border border-border p-3 space-y-3" data-testid="portal-snooze-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs mb-1 block">First day away</Label>
                                <Input
                                    type="date"
                                    value={start}
                                    min={todayIso()}
                                    onChange={(e) => setStart(e.target.value)}
                                    data-testid="portal-snooze-start"
                                />
                            </div>
                            <div>
                                <Label className="text-xs mb-1 block">Last day away</Label>
                                <Input
                                    type="date"
                                    value={end}
                                    min={start || todayIso()}
                                    onChange={(e) => setEnd(e.target.value)}
                                    data-testid="portal-snooze-end"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Note (optional)</Label>
                            <Input
                                placeholder="e.g. Beach trip"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                maxLength={120}
                                data-testid="portal-snooze-reason"
                            />
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowForm(false)}
                                data-testid="portal-snooze-cancel"
                            >
                                <X className="w-3.5 h-3.5 mr-1" />
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={add}
                                disabled={saving}
                                data-testid="portal-snooze-save"
                            >
                                {saving ? "Adding…" : "Snooze these days"}
                            </Button>
                        </div>
                    </div>
                )}

                {past.length > 0 && (
                    <details className="mt-3 text-xs text-muted-foreground">
                        <summary className="cursor-pointer select-none">
                            {past.length} past snooze{past.length === 1 ? "" : "s"}
                        </summary>
                        <div className="mt-2 space-y-1">
                            {past.slice(-5).reverse().map((s) => (
                                <div key={s.id} className="tabular">{fmtRange(s.start_date, s.end_date)}{s.reason ? ` — ${s.reason}` : ""}</div>
                            ))}
                        </div>
                    </details>
                )}
            </CardContent>
        </Card>
    );
}
